"use client";

import React, { useState } from "react";
import { Funnel, ChevronDown, Weight, Ruler, Mountain, Zap, Activity, Search, X, Menu, Timer, Wind, Trophy } from "lucide-react";
import { Sidebar, SidebarContent, SidebarGroup, SidebarGroupContent, SidebarGroupLabel, SidebarHeader, SidebarInset, SidebarMenu, SidebarMenuItem, SidebarMenuButton, SidebarProvider, SidebarTrigger, useSidebar } from "@/components/ui/sidebar";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import { EcommerceProductsProvider, useEcommerceProducts, AddToCartButton } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
function TechSpecBadge({
  icon: Icon,
  label,
  value
}: {
  icon: any;
  label: string;
  value: string;
}) {
  return <div className="flex flex-col items-center justify-center gap-1.5 p-2 rounded-md bg-muted/20 border border-border/10 hover:border-primary/30 hover:bg-primary/5 transition-all group/badge">
      <Icon className="size-3.5 text-muted-foreground group-hover/badge:text-primary transition-colors" />
      <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground group-hover/badge:text-foreground transition-colors">{value}</span>
    </div>;
}
function ProductCard({
  product,
  index
}: {
  product: any;
  index: number;
}) {
  const weight = product.customFields?.weight || `${200 + index * 15 % 100}g`;
  const drop = product.customFields?.drop || `${4 + index * 2 % 8}mm`;
  const terrain = product.customFields?.terrain || (index % 2 === 0 ? "Track" : "Road");
  return <Card className="group relative overflow-hidden border-border/40 bg-card/40 backdrop-blur-sm hover:border-primary/50 hover:shadow-[0_0_40px_-15px_hsl(var(--primary)/0.2)] transition-all duration-500 h-full flex flex-col rounded-xl">
      <div className="absolute top-0 right-0 p-3 z-10 flex gap-2">
        {index % 3 === 0 && <span className="inline-flex items-center rounded-full border border-primary/30 bg-primary/10 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-primary backdrop-blur-md shadow-[0_0_10px_-2px_hsl(var(--primary)/0.3)]">
            <Zap className="mr-1 size-3" />
            New Drop
          </span>}
      </div>
      
      <Link to={product.link} className="block flex-1">
        <div className="aspect-[4/3] overflow-hidden bg-muted/10 relative group-hover:bg-muted/20 transition-colors">
          {product.featured_image_url ? <img src={product.featured_image_url} alt={product.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105 group-hover:rotate-1" /> : <div className="h-full w-full flex items-center justify-center bg-muted/5">
              <Activity className="size-12 text-muted-foreground/20" />
            </div>}
          
          {}
          <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4">
             <p className="text-xs text-primary font-mono font-bold tracking-widest flex items-center gap-2">
               <Search className="size-3" />
               VIEW TECH SPECS
             </p>
          </div>
        </div>

        <CardHeader className="p-5 pb-2 space-y-1">
          <CardTitle className="text-lg font-bold tracking-tight line-clamp-1 group-hover:text-primary transition-colors">
            {product.title}
          </CardTitle>
          <div className="flex items-baseline gap-2">
            <span className="text-xl font-bold text-foreground font-mono tracking-tight">${product.price}</span>
            {product.regular_price && product.price < product.regular_price && <span className="text-sm text-muted-foreground line-through font-mono">${product.regular_price}</span>}
          </div>
        </CardHeader>

        <CardContent className="p-5 pt-2">
          {}
          <div className="grid grid-cols-3 gap-2 mt-2">
            <TechSpecBadge icon={Weight} label="Weight" value={weight} />
            <TechSpecBadge icon={Ruler} label="Drop" value={drop} />
            <TechSpecBadge icon={Mountain} label="Terrain" value={terrain} />
          </div>
        </CardContent>
      </Link>

      <CardFooter className="p-5 pt-0 mt-auto">
        <AddToCartButton productId={product.id} variant="default" className="w-full" buttonClassName="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-bold tracking-wide uppercase h-10 shadow-[0_0_20px_-5px_hsl(var(--primary)/0.3)] hover:shadow-[0_0_25px_-5px_hsl(var(--primary)/0.5)] transition-all" text="Add to Cart" showQuantity={false} />
      </CardFooter>
    </Card>;
}
function CatalogContent() {
  const {
    products,
    loading,
    error,
    refetch
  } = useEcommerceProducts();
  if (loading) {
    return <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-6 p-6">
        {[...Array(8)].map((_, i) => <div key={i} data-index={i} className="h-[420px] rounded-xl bg-muted/5 animate-pulse border border-border/20" />)}
      </div>;
  }
  if (error) {
    return <div className="flex flex-col items-center justify-center h-96 text-center p-6 border border-destructive/20 rounded-xl bg-destructive/5 m-6">
        <Activity className="size-12 text-destructive mb-4" />
        <h3 className="text-xl font-bold mb-2 text-destructive">System Malfunction</h3>
        <p className="text-muted-foreground mb-6">Unable to load catalog data.</p>
        <Button onClick={() => refetch()} variant="outline" className="border-destructive/50 hover:bg-destructive/10 text-destructive">Retry Connection</Button>
      </div>;
  }
  if (products.length === 0) {
    return <div className="flex flex-col items-center justify-center h-96 text-center p-6 border border-border/20 rounded-xl bg-muted/5 m-6">
        <Search className="size-12 text-muted-foreground mb-4" />
        <h3 className="text-xl font-bold mb-2">No Results Found</h3>
        <p className="text-muted-foreground">Try adjusting your filters.</p>
      </div>;
  }
  return <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-6 p-6">
      {products.map((product, i) => <div key={product.id} data-index={i}>
          <ProductCard product={product} index={i} />
        </div>)}
    </div>;
}
function FilterCheckbox({
  id,
  label
}: {
  id: string;
  label: string;
}) {
  return <div className="flex items-center space-x-3 py-1.5 group">
      <Checkbox id={id} className="border-muted-foreground/40 data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground data-[state=checked]:border-primary size-4 rounded-[2px] transition-all duration-300 group-hover:border-primary/50" />
      <label htmlFor={id} className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 text-muted-foreground group-hover:text-foreground transition-colors cursor-pointer">
        {label}
      </label>
    </div>;
}
function CatalogSidebar() {
  return <Sidebar className="!absolute !top-0 !left-0 !h-full border-r border-border/10 bg-background/80 backdrop-blur-xl z-20" collapsible="offcanvas">
      <SidebarHeader className="h-16 flex items-center px-6 border-b border-border/10">
        <div className="flex items-center gap-2 font-bold text-xl tracking-tighter text-foreground">
          <Funnel className="size-5 text-primary" />
          <span>FILTERS</span>
        </div>
      </SidebarHeader>
      <SidebarContent className="px-6 py-8 gap-8">
        <SidebarGroup>
          <SidebarGroupLabel className="text-primary font-mono text-[10px] uppercase tracking-[0.2em] mb-4 flex items-center gap-2">
            <Trophy className="size-3" /> Category
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <div className="space-y-2">
              <FilterCheckbox id="cat-sprint" label="Sprint Spikes" />
              <FilterCheckbox id="cat-distance" label="Distance & Marathon" />
              <FilterCheckbox id="cat-trail" label="Trail & Off-road" />
              <FilterCheckbox id="cat-training" label="Daily Training" />
            </div>
          </SidebarGroupContent>
        </SidebarGroup>

        <SidebarGroup>
          <SidebarGroupLabel className="text-primary font-mono text-[10px] uppercase tracking-[0.2em] mb-4 flex items-center gap-2">
            <Zap className="size-3" /> Technology
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <div className="space-y-2">
              <FilterCheckbox id="tech-carbon" label="Carbon Plate" />
              <FilterCheckbox id="tech-foam" label="React Foam" />
              <FilterCheckbox id="tech-air" label="Zoom Air Unit" />
              <FilterCheckbox id="tech-knit" label="FlyKnit Upper" />
            </div>
          </SidebarGroupContent>
        </SidebarGroup>

        <SidebarGroup>
          <SidebarGroupLabel className="text-primary font-mono text-[10px] uppercase tracking-[0.2em] mb-4 flex items-center gap-2">
            <Mountain className="size-3" /> Terrain
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <div className="space-y-2">
              <FilterCheckbox id="terrain-track" label="Track" />
              <FilterCheckbox id="terrain-road" label="Road" />
              <FilterCheckbox id="terrain-trail" label="Trail" />
              <FilterCheckbox id="terrain-gym" label="Gym / Indoor" />
            </div>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
    </Sidebar>;
}
export default function SpeedAgilityCatalog() {
  const wp_query_params = {
    per_page: 12,
    status: "publish",
    orderby: "date",
    order: "desc"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="speed-agility-catalog" className="dark bg-background text-foreground relative overflow-hidden min-h-screen">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none z-0">
        <div className="absolute top-[-20%] right-[-10%] w-[800px] h-[800px] bg-primary/5 rounded-full blur-[120px] opacity-50" />
        <div className="absolute bottom-[-20%] left-[-10%] w-[600px] h-[600px] bg-secondary/5 rounded-full blur-[100px] opacity-50" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full bg-[url('https://wpvc-images.s3.us-east-1.amazonaws.com/images/1564626/img/grid_pattern.png')] bg-repeat opacity-[0.02]" />
      </div>

      <EcommerceProductsProvider wp_query={{
      per_page: 12,
      status: ["publish"],
      orderby: "date",
      order: "desc"
    }} postType="product" className="relative z-10 h-full">
        <SidebarProvider className="min-h-[800px] h-full w-full">
          <CatalogSidebar />
          
          <SidebarInset className="bg-transparent min-h-[800px] flex flex-col">
            {}
            <header className="sticky top-0 z-30 flex h-16 items-center gap-4 border-b border-border/10 bg-background/80 px-6 backdrop-blur-xl">
              <SidebarTrigger className="text-foreground hover:text-primary hover:bg-primary/10 transition-colors" />
              <div className="h-4 w-px bg-border/20" />
              <h2 className="text-lg font-bold tracking-tight uppercase">Speed & Agility Catalog</h2>
              <div className="ml-auto flex items-center gap-4">
                <span className="text-[10px] text-muted-foreground hidden sm:inline-flex items-center gap-2 font-mono uppercase tracking-wider border border-border/20 px-2 py-1 rounded-full">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-primary"></span>
                  </span>
                  Live Inventory
                </span>
                <Button variant="outline" size="sm" className="hidden sm:flex border-border/30 hover:border-primary/50 hover:text-primary hover:bg-primary/5 text-xs font-mono uppercase tracking-wider">
                  Sort by: Newest
                  <ChevronDown className="ml-2 size-3" />
                </Button>
              </div>
            </header>

            {}
            <main className="relative flex-1">
              <CatalogContent />
            </main>
          </SidebarInset>
        </SidebarProvider>
      </EcommerceProductsProvider>
    </section>;
}